import { vec2 } from "@tlaukkan/tsm";
import { Entity, System } from "ecsy";
import * as PIXI from 'pixi.js';
import { Antibody, BloodCell, Buff, Buffed, BuffPreview, Collision, Consumer, Cooldown, Dead, ElectricCharge, ElectricConductor, Flashing, Infected, InfectionGrowth, Inflating, Jump, JustCombined, OnBuy, OnBuyOther, OnConsume, OnNearTarget, Pop, PreviewOnly, Promoted, Renderable, Spawner, Submerged, TaggedVirus, Transform, Triggered, UnitOfElectricCurrent, Virus, VirusLoadIncrease, VirusPointAttractor } from "../../../shared/components";
import { getEntityPlayer } from "../../../shared/composition";
import { GameElement } from "../../../shared/gameElementTypes";
import { canGroupPromote, findAlikeElements } from "../../../shared/player";
import { RenderEffects } from "../../../shared/constants";
import { getEntityById, getId } from "../../../shared/entityFactory";
import { HighlightType } from "../../../shared/highlightType";
import { lerp } from "../../../shared/interpolation";
import { animate2D } from "../../../shared/systems/animationSystem";
import { ElectricitySystem } from "../../../shared/systems/electricitySystem";
import { selectAndFilter, TargetType, TriggerScope } from "../../../shared/triggeredEffects";
import { addComponent, getOrAddComponent, getSystem, lerpColor, perpendicular, removeComponentIfPresent, seededRandom, worldToScreenPosition } from "../../../shared/utils";
import { GameClient } from "../gameClient";
import { Colors } from "../palettes/colorPalette";
import { RenderEngine } from "../rendering/renderEngine";
import { OverlayInfoSystem } from "./overlayInfoSystem";

/** Flashing's own default color, for the plain (not golden) combine preview */
const DefaultFlashColor = new Flashing().color;

export class EntityVFXSystem extends System {
	private game: GameClient;
	private engine: RenderEngine;
	private electricitySystem!: ElectricitySystem;
	/** Cells to flash as combine targets while dragging, and in what color. Applied once a frame, see applyPromotePreview */
	private promotePreview = new Map<Entity, number>();
	/** Cells currently flashing because of promotePreview */
	private promotePreviewFlashing = new Set<Entity>();

	constructor(world: any, attributes: any) {
		super(world, attributes);
		this.game = attributes.game;
		this.engine = this.game.renderEngine
	}

	init() {
		this.electricitySystem = getSystem(this.world, ElectricitySystem);
	}



	execute(delta: number, time: number) {
		//Indicate tag
		for (const entity of this.queries.tagged.added || []) {
			const renderable = entity.getComponent(Renderable)
			renderable.highlight = HighlightType.Tagged;
		}
		for (const entity of this.queries.tagged.removed || []) {
			const renderable = entity.getComponent(Renderable)
			if (renderable) {
				renderable.highlight = HighlightType.None;
			}
		}

		this.queries.collision.added?.forEach(entity => {
			const collision = entity.getComponent(Collision);
			const pos = entity.getComponent(Transform).position.copy();
			const hit = collision.collidedWith[0].entity.getComponent(Transform).position.copy();
			const stretch = hit.subtract(pos).normalize().scale(RenderEffects.SquashAndStretchRadiusPercentage);
			this.squashAndStretchSprite(entity, stretch);
		})

		//Promote preview flashing
		const preview = this.promotePreview;

		for (const entity of this.promotePreviewFlashing) {
			if (!preview.has(entity)) removeComponentIfPresent(entity, Flashing);
		}
		this.promotePreviewFlashing.clear();

		for (const [entity, color] of preview) {
			if (!entity.hasComponent(Renderable)) continue; //destroyed since the preview was made, eg combined
			const flash = getOrAddComponent(entity, Flashing);
			flash.color = color;
			flash.times = -1; //for as long as it's in the preview, also taking over any one off flash the cell had
			this.promotePreviewFlashing.add(entity);
		}

		const doBuffEffect = (entity: Entity) => {
			if (getId(entity) === null) return; //guard against death
			addComponent(entity, Flashing, { lengthSec: 0.5, color: 0xeeeeee, times: 1 })
			addComponent(entity, Pop, { stretchPercent: new vec2([0.2, 0.2]), durationMS: 120, delayMS: 200 });
		};
		this.queries.buffed.added?.forEach(entity => {

			doBuffEffect(entity);
		});
		this.queries.buffed.changed?.forEach(entity => {
			doBuffEffect(entity);
		});

		this.queries.combined.added?.forEach(entity => {
			addComponent(entity, Flashing, { lengthSec: 0.5, color: HighlightType.Golden, times: 1 })
			addComponent(entity, Pop, { stretchPercent: new vec2([0.2, 0.2]), durationMS: 120, delayMS: 200 });
		});

		this.queries.triggered.results.forEach(entity => {
			addComponent(entity, Flashing, { lengthSec: 0.5, color: 0xeeeeee, times: 1 });
		});

		for (const entity of this.queries.cooldown.results) {
			const cooldown = entity.getComponent(Cooldown);
			if (cooldown.done) continue;

			if (!entity.hasComponent(Virus)) { //viruses shouldn't inflate
				const inflation = getOrAddComponent(entity, Inflating);
				inflation.inflation = 1 - (cooldown.remaining / cooldown.cooldown);
			}
		};

		for (const entity of this.queries.onNearTarget.results) {
			const onNearTarget = entity.getComponent(OnNearTarget);
			const remainingDistance = onNearTarget.currentDistance - onNearTarget.thresholdDistance;
			if (remainingDistance < 6 && !entity.hasComponent(Flashing)) {
				addComponent(entity, Flashing, {
					lengthSec: 0.1,
					color: 0xffffff,
				});
			}
		}

		for (const entity of this.queries.consumer.results) {
			const consumer = entity.getComponent(Consumer);
			const onConsume = entity.getComponent(OnConsume);
			const inflation = getOrAddComponent(entity, Inflating);
			let progress = 0;
			const totalRequiredCount = onConsume.requiredAmount.map(entry => entry.amount).reduce((a, b) => a + b, 0);
			for (let i = 0; i < consumer.consumedAmounts.length; i++) {
				const amount = consumer.consumedAmounts[i];
				const type = consumer.types[i];
				const required = onConsume.requiredAmount.find(entry => entry.type === type)?.amount || 1;
				const completeFract = Math.min(amount, required) / required;
				const typeFraction = required / totalRequiredCount;
				progress += completeFract * typeFraction;
			}
			inflation.inflation = progress;
		};

		const removePop = new Array<Entity>();
		this.queries.pop.results.forEach(entity => {
			const pop = entity.getComponent(Pop);
			if (pop.delayMS > 0) {
				pop.delayMS -= delta;
			}
			else {
				this.squashAndStretchSprite(entity, pop.stretchPercent, pop.durationMS);
				removePop.push(entity);
			}
		})
		removePop.forEach(entity => entity.removeComponent(Pop));

		//submerged effect
		for (const entity of this.queries.submerged.results) {
			const submerge = entity.getComponent(Submerged);
			const renderable = entity.getComponent(Renderable);

			const darkness = lerpColor(0xffffff, 0x000000, submerge.percentSubmerged)
			renderable.highlight = darkness;
			renderable.sprite.alpha = 1 - (submerge.percentSubmerged * 2);
			renderable.depthSprite.alpha = 1 - (submerge.percentSubmerged * 2);
			renderable.trailScale = 1 - submerge.percentSubmerged;
			if (renderable.membrane) {
				renderable.membrane.alpha = 1 - submerge.percentSubmerged;
			}
			if (!submerge.dontAffectScale) {
				const reducedScale = Math.max(-1, -1 * (submerge.percentSubmerged * 2));
				renderable.addedScale = new vec2([reducedScale, reducedScale]);
			}
		}
		this.queries.submerged.removed?.forEach(entity => {
			const submerge = entity.getRemovedComponent(Submerged);
			const renderable = entity.getComponent(Renderable);
			if (renderable) {
				renderable.highlight = HighlightType.None;
				renderable.sprite.alpha = 1;
				if (submerge && !submerge.dontAffectScale) {
					renderable.addedScale = vec2.zero.copy();
				}
			}
		});

		//Infected cells pulse
		for (const entity of this.queries.infected.results) {
			const spawner = entity.getComponent(Spawner);
			if (spawner.spawningNow) {
				const stretch = new vec2([0.2, 0.2]);
				this.squashAndStretchSprite(entity, stretch);
			}
			//If spawning yet to come, do the periodic pulse
			else if (spawner.amount > 0) {
				const period = Math.floor(seededRandom(entity.id) * 2000);
				if (Math.floor(time) % period === 0) {
					const stretch = new vec2([0.1, 0.1]);
					this.squashAndStretchSprite(entity, stretch);
				}
			}
		}

		//Flashing effect 
		const removeFlash = new Array<Entity>();
		for (const entity of this.queries.flashing.results) {
			const renderable = entity.getComponent(Renderable);
			const flash = entity.getComponent(Flashing);
			const total = flash.lengthSec * 2;
			const deltaS = (delta / 1000);
			flash.progress = (flash.progress + deltaS);
			if (flash.progress >= total) {
				--flash.times;
			}
			flash.progress = flash.progress % total;
			let tintProgress = flash.progress / flash.lengthSec;
			if (flash.progress > flash.lengthSec) {
				//Going down
				tintProgress = 2 - tintProgress;
			}
			tintProgress = lerp(0.1, 0.85, tintProgress);
			renderable.highlight = HighlightType.Effect;
			renderable.glowSprite.tint = flash.color;
			renderable.glowSprite.alpha = tintProgress;
			if (flash.times === 0) {
				removeFlash.push(entity);
			}
		}
		removeFlash.forEach(entity => entity.removeComponent(Flashing));

		this.queries.flashing.removed?.forEach(entity => {
			const renderable = entity.getComponent(Renderable);
			if (renderable) {
				renderable.highlight = HighlightType.None;
			}
		});

		if (this.game.getCurrentState() === 'simulation') { //no inflating when in shop even though cooldown are maxed
			for (const entity of this.queries.inflating.results) {
				const inflation = entity.getComponent(Inflating);
				const renderable = entity.getComponent(Renderable);
				const submerged = entity.hasComponent(Submerged);
				if (submerged) continue;
				//renderable.sprite.shader.uniforms.inflation = easeInCubic(0, 1, inflation.inflation); //TODO: Must restore inflation effect...
				const addedScale = inflation.inflation * 0.3;
				renderable.addedScale.xy = [addedScale, addedScale];
			}
		}

		for (const entity of this.queries.virusAttractors.results) {
			const attractor = entity.getComponent(VirusPointAttractor);
			if (attractor.joinedEntities.size > 0) continue;
			const point = attractor.attractorPoint;
			const pos = this.engine.getScreenCoordinate(worldToScreenPosition(point));
			this.engine.graphicsOverlay.beginFill(0xffffff);
			this.engine.graphicsOverlay.drawCircle(pos.x, pos.y, 2);
			this.engine.graphicsOverlay.endFill();
		}

		//Buy Buff preview stuff, kinda janky, relies on scanning onbuy previews

		const incomingBuffs = new Set<Entity>();
		const outgoingBuffs = new Set<Entity>();

		//If an element is being previewed for purchase, read triggers, work out targets, and sort into outgoing and incoming
		let currentPreview: Entity | null = null;
		for (const entity of this.queries.previews.results) {
			if (getEntityPlayer(entity, this.game) === this.game.getPlayer()) {
				currentPreview = entity;
			}
		}

		if (currentPreview) {
			const previewHasOnBuy = currentPreview.hasComponent(OnBuy);

			if (previewHasOnBuy) {
				const onbuy = currentPreview.getComponent(OnBuy);
				if (onbuy.effect.component.component === Buff) {
					//Get target of buff
					const creator = currentPreview;
					const effect = onbuy.effect;
					const selector = effect.targetSelector || { type: TargetType.Self };
					const filters = effect.filters || [];
					const targets = selectAndFilter(selector, filters, creator, this.game);

					//Play incoming buff preview
					for (const target of targets) {
						incomingBuffs.add(target);
					}

					if (targets.length > 0) {
						//Play outgoing buff preview
						outgoingBuffs.add(currentPreview);
					}
				}
			}

			for (const entity of this.queries.onBuyOthers.results) {
				if (entity === currentPreview) continue;
				const onbuyother = entity.getComponent(OnBuyOther);
				if (onbuyother.effect.component.component === Buff) {

					//Get target of buff
					const creator = entity;
					const effect = onbuyother.effect;
					const selector = effect.targetSelector || { type: TargetType.Self };
					const filters = effect.filters || [];
					const targets = selectAndFilter(selector, filters, creator, this.game, currentPreview);
					if (targets.length > 0) {
						//Play incoming buff preview
						for (const target of targets) {
							incomingBuffs.add(target);
						}

						if (!targets.includes(entity)) {
							outgoingBuffs.add(entity);
						}
					}
				}
			}
		}

		//Remove expired buff preview components and mark ones that are already active as handled
		const removeBuffPreview = new Array<Entity>();
		for (const entity of this.queries.buffPreview.results) {
			const buffPreview = entity.getComponent(BuffPreview);
			if (!buffPreview.isOutgoing && !incomingBuffs.has(entity)) {
				removeBuffPreview.push(entity);
			}
			if (buffPreview.isOutgoing && !outgoingBuffs.has(entity)) {
				removeBuffPreview.push(entity);
			}

			outgoingBuffs.delete(entity);
			incomingBuffs.delete(entity);
		}
		removeBuffPreview.forEach(entity => removeComponentIfPresent(entity, BuffPreview));

		//Anything left in the incoming/outgoing sets is new, so add the preview component
		for (const entity of incomingBuffs) {
			//TODO: Half-assed this, We are only buffing health right now, but this could change. This value affects particle buff color
			//Possibly could not color code buff previews though.
			addComponent(entity, BuffPreview, { isOutgoing: false, stat: 'health' });
		}
		for (const entity of outgoingBuffs) {
			addComponent(entity, BuffPreview, { isOutgoing: true, stat: 'health' });
		}


		//Electricity preview 
		const LIGHTNING_BOLT_FRAMES = 7;
		const hovered = this.game.hoveredEntity !== null ? getEntityById(this.game.hoveredEntity) : null;
		const shouldDrawConnections = this.game.inSimulation == false && hovered && this.queries.electric.results.includes(hovered);
		for (const entity of this.queries.electric.results) {
			const electric = entity.getComponent(ElectricConductor);

			if (shouldDrawConnections) {
				//draw an arrow along each outgoing edge of each conductor
				for (const outgoing of electric.outgoing) {
					const from = entity.getComponent(Transform);
					const to = outgoing.getComponent(Transform);
					if (!to || !from) continue;

					const start = this.engine.getScreenCoordinate(worldToScreenPosition(from.position));
					const end = this.engine.getScreenCoordinate(worldToScreenPosition(to.position));

					this.drawLighting(start, end, 1);
				}
			}
		}

		for (const shock of this.electricitySystem.allCurrent) {
			for (let i = 0; i < shock.pathTraveled.length - 1 && i < LIGHTNING_BOLT_FRAMES; i++) {
				const from = shock.pathTraveled[i + 1];
				const to = shock.pathTraveled[i];
				const fromTransform = from.getComponent(Transform);
				const toTransform = to.getComponent(Transform);
				if (!toTransform || !fromTransform) continue;

				const start = this.engine.getScreenCoordinate(worldToScreenPosition(fromTransform.position));
				const end = this.engine.getScreenCoordinate(worldToScreenPosition(toTransform.position));
				const weight = (LIGHTNING_BOLT_FRAMES) - (i);
				//Draw a jagged lighting like path between start and end
				this.drawLighting(start, end, weight)
			}
		}
		for (const sunkCurrent of this.electricitySystem.recentlySunkCurrent) {
			const start = this.engine.getScreenCoordinate(worldToScreenPosition(sunkCurrent.position));
			const end = this.engine.getScreenCoordinate(worldToScreenPosition(sunkCurrent.position));
			const weight = (LIGHTNING_BOLT_FRAMES);
			//Draw a jagged lighting like path between start and end
			this.drawLighting(start, end, weight)
		}

		for (const entity of this.queries.infectionGrowth.results) {
			const transform = entity.getComponent(Transform); //not in query so we can use changed event..
			const renderable = entity.getComponent(Renderable);
			if (!transform || !renderable) return;
			this.squashAndStretchSprite(entity, new vec2([0.1, 0.1]), 1);
		};
	}

	async squashAndStretchSprite(entity: Entity, stretch: vec2, duration = RenderEffects.SquashAndStretchTotalDuration) {
		const renderable = entity.getComponent(Renderable);
		await animate2D(renderable, 'addedScale', stretch.copy().negate(), duration / 2);
		await animate2D(renderable, 'addedScale', stretch, duration / 4);
		animate2D(renderable, 'addedScale', vec2.zero.copy(), duration / 4);
	}

	drawLighting(start: vec2, end: vec2, thickness: number) {
		const arrow = end.copy().subtract(start);
		const length = arrow.length();
		const afterstart = arrow.copy().normalize().scale(length * 0.2).add(start);
		const beforeend = arrow.copy().normalize().scale(length * 0.8).add(start);
		const mid = arrow.copy().normalize().scale(length * 0.5).add(start);
		const perp = perpendicular(arrow.normalize());
		afterstart.add(perp.copy().scale((Math.random() * 20) - 10));
		mid.add(perp.copy().scale((Math.random() * 20) - 10));
		beforeend.add(perp.copy().scale((Math.random() * 20) - 10));

		this.engine.graphicsOverlay.lineStyle(thickness, Colors.Electricity, 1);
		this.engine.graphicsOverlay.moveTo(start.x, start.y);
		this.engine.graphicsOverlay.lineTo(afterstart.x, afterstart.y);
		this.engine.graphicsOverlay.lineTo(mid.x, mid.y);
		this.engine.graphicsOverlay.lineTo(beforeend.x, beforeend.y);
		this.engine.graphicsOverlay.lineTo(end.x, end.y);
	}

	drawDebugArrow(start: vec2, end: vec2) {
		const arrow = end.copy().subtract(start);
		const length = arrow.length();
		const arrowSize = 20; // Adjust the size of the arrow as needed
		const arrowHeadLength = arrowSize * 2; // Adjust the length of the arrow head as needed

		// Calculate the position of the arrow head
		const arrowHeadPosition = arrow.copy().normalize().scale(length - arrowHeadLength).add(start);

		// Draw the arrow line
		this.engine.graphicsOverlay.lineStyle(2, Colors.Electricity, 1);
		this.engine.graphicsOverlay.moveTo(start.x, start.y);
		this.engine.graphicsOverlay.lineTo(arrowHeadPosition.x, arrowHeadPosition.y);

		// Draw the arrow head
		const arrowHeadDirection = arrow.copy().normalize().scale(arrowHeadLength);
		const arrowHeadPerpendicular = perpendicular(arrowHeadDirection.normalize()).scale(arrowSize * 0.5);
		const arrowHeadPoint1 = arrowHeadPosition.copy().add(arrowHeadDirection).add(arrowHeadPerpendicular);
		const arrowHeadPoint2 = arrowHeadPosition.copy().add(arrowHeadDirection).subtract(arrowHeadPerpendicular);
		this.engine.graphicsOverlay.lineTo(arrowHeadPoint1.x, arrowHeadPoint1.y);
		this.engine.graphicsOverlay.lineTo(end.x, end.y);
		this.engine.graphicsOverlay.lineTo(arrowHeadPoint2.x, arrowHeadPoint2.y);
		this.engine.graphicsOverlay.lineTo(arrowHeadPosition.x, arrowHeadPosition.y);
	}

	updatePromotePreview(type: GameElement | null, selected: Entity | null = null, isPreview: boolean = false) {
		this.promotePreview = new Map();

		//We never do anything if selected is already tripled
		if (selected) {
			const comp = this.game.getEntityCompElement(selected);
			if (comp && comp.promoted) return;
		}

		if (type !== null) { //not a falsy check, SIMPLE is 0
			const alikeGroup = findAlikeElements(this.game.getPlayer()!, type);
			if (canGroupPromote(alikeGroup, !isPreview ? 0 : 1)) {
				alikeGroup.forEach(el => {
					const entity = getEntityById(el.id);
					if (entity) {
						this.promotePreview.set(entity, HighlightType.Golden);
					}
				});
				if (selected !== null) {
					this.promotePreview.set(selected, HighlightType.Golden);
				}
			}
			else {
				for (const element of alikeGroup) {
					const entity = getEntityById(element.id);
					if (entity && entity !== selected) {
						this.promotePreview.set(entity, DefaultFlashColor);
					}
				}
			}
		}
	}
}


EntityVFXSystem.queries = {
	collision: { components: [Antibody, Renderable, Collision], listen: { added: true } },
	tagged: { components: [Renderable, TaggedVirus], listen: { added: true } },
	jump: { components: [Renderable, Jump], listen: { added: true } },
	promoted: { components: [Renderable, Promoted] },
	submerged: { components: [Renderable, Submerged], listen: { removed: true } },
	infected: { components: [Renderable, Infected, Spawner] },
	flashing: { components: [Renderable, Flashing], listen: { removed: true } },
	inflating: { components: [Renderable, Inflating] },
	pop: { components: [Renderable, Pop], listen: { added: true } },
	buffed: { components: [Buffed], listen: { added: true, changed: true } },
	combined: { components: [JustCombined], listen: { added: true } },
	triggered: { components: [Renderable, Triggered], listen: { added: true } },
	cooldown: { components: [Renderable, Cooldown] },
	consumer: { components: [Renderable, Consumer, OnConsume] },
	virusAttractors: { components: [Renderable, VirusPointAttractor] },
	onNearTarget: { components: [Renderable, OnNearTarget] },
	previews: { components: [Renderable, Transform, PreviewOnly] },
	onBuyOthers: { components: [Renderable, OnBuyOther, Transform] },
	buffPreview: { components: [Renderable, BuffPreview] },
	electric: { components: [Renderable, Transform, ElectricConductor] },
	deadVirus: { components: [Renderable, Virus, Dead] },
	infectionGrowth: {
		components: [InfectionGrowth, VirusLoadIncrease]
	},
}